import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, SITE } from "../data/site";
import { useSettings } from "../hooks/useCms";

const LogoMark = ({ light = false }) => {
  const introDelay =
    typeof window !== "undefined" &&
    !sessionStorage.getItem("oph_intro") &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? 3.1
      : 0;
  return (
    <Link to="/" className="relative flex items-center group" data-testid="nav-logo" aria-label="On Point Homes — home">
      <motion.img
        src="/logo-horizontal.webp"
        alt="On Point Homes — Idaho home builder logo"
        className={`h-14 md:h-16 w-auto ${light ? "invert" : ""}`}
        initial={{ clipPath: "inset(0 100% 0 0)" }}
        animate={{ clipPath: "inset(0 0% 0 0)" }}
        transition={{ duration: 2.0, delay: introDelay, ease: [0.65, 0, 0.35, 1] }}
        whileHover={{ scale: 1.04, rotate: -1 }}
        whileTap={{ scale: 0.95 }}
      />
    </Link>
  );
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const settings = useSettings();
  const links = settings.nav?.length ? settings.nav.filter((n) => n.visible !== false) : NAV_LINKS;
  const phone = settings.phone || SITE.phone;
  const region = settings.region || SITE.region;
  const ctaLabel = settings.default_cta_label || "Start Your Project";
  const ctaLink = settings.default_cta_link || "/contact";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow] duration-500 ${
          scrolled ? "bg-[#F7F5F2]/90 backdrop-blur-md shadow-[0_1px_0_rgba(26,26,26,0.08)]" : "bg-[#F7F5F2]/70 backdrop-blur-sm"
        }`}
      >
        <div className="max-w-[1600px] mx-auto pl-5 md:pl-10 pr-4 md:pr-5 h-16 md:h-20 flex items-center justify-between">
          <LogoMark />
          <nav className="hidden lg:flex items-center gap-10 flex-1 justify-end pl-24" aria-label="Primary">
            {links.filter((l) => l.to !== "/contact").map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `link-underline font-mono text-[0.95rem] tracking-[0.14em] uppercase font-semibold transition-colors duration-300 ${
                    isActive ? "text-[#8C6A4F]" : "text-[#1A1A1A] hover:text-[#8C6A4F]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to={ctaLink}
              data-testid="nav-start-project-btn"
              className="btn-sweep bg-[#1A1A1A] text-[#F7F5F2] font-mono text-[0.9rem] tracking-[0.14em] uppercase font-semibold px-8 py-4 transition-colors duration-300"
            >
              {ctaLabel}
            </Link>
          </nav>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 text-[#1A1A1A]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-40 bg-[#1A1A1A] pt-24 px-8 flex flex-col"
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="block font-serif text-4xl text-[#F7F5F2] py-3 border-b border-white/10"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="mt-auto pb-10">
              <p className="eyebrow text-[#A3A09B] mb-2">{region}</p>
              <p className="font-mono text-xs text-[#A3A09B] tracking-widest">{phone}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
